import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import {
  getBrands,
  getBrandById,
  getBrandDeposit,
  getBrandMemberList,
  addDeposit,
  addLicense,
  createBrand,
  impersonateBrand,
  updateBrandLogo,
} from '../../services/BrandService';
import {
  getBrandTotals,
  uploadBrandLogoToS3,
} from '../../services/userService';

const STALE_TIME = 30_000;

export const useGetBrandsQuery = (
  page: number,
  limit: number,
  searchTerm?: string,
) =>
  useQuery({
    queryKey: queryKeys.brands.list(page, limit, searchTerm),
    queryFn: () => getBrands(page, limit, searchTerm || ''),
    staleTime: STALE_TIME,
  });

export const useGetBrandByIdQuery = (brandId: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.brands.byId(brandId),
    queryFn: () => getBrandById(brandId),
    staleTime: STALE_TIME,
    enabled: enabled && !!brandId,
  });

export const useGetBrandDepositQuery = (
  brandId: string,
  page: number,
  limit: number,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.brands.deposit(brandId, page, limit),
    queryFn: () => getBrandDeposit(brandId, page, limit),
    staleTime: STALE_TIME,
    enabled: enabled && !!brandId,
  });

export const useGetBrandMemberListQuery = (
  brandId: string,
  page: number,
  limit: number,
  searchTerm?: string,
) =>
  useQuery({
    queryKey: queryKeys.brands.memberList(brandId, page, limit, searchTerm),
    queryFn: () => getBrandMemberList(page, limit, searchTerm, brandId),
    staleTime: STALE_TIME,
    enabled: !!brandId,
  });

export const useAddDepositMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, formData }: { brandId: string; formData: any }) =>
      addDeposit(brandId, formData),
    onSuccess: (_data, { brandId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.user.totals(brandId),
      });
      queryClient.invalidateQueries({
        queryKey: ['deposits', brandId],
      });
      queryClient.invalidateQueries({
        queryKey: ['brands', 'deposit', brandId],
      });
    },
  });
};

export const useAddLicenseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, formData }: { brandId: string; formData: any }) =>
      addLicense(brandId, formData),
    onSuccess: (_data, { brandId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.brands.byId(brandId),
      });
    },
  });
};

export const useCreateBrandMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: any) => createBrand(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['brands', 'list'],
      });
    },
  });
};

export const useBrandTotalsQuery = (brandId: string) =>
  useQuery({
    queryKey: queryKeys.user.totals(brandId),
    queryFn: () => getBrandTotals(brandId),
    staleTime: STALE_TIME,
    enabled: !!brandId,
  });

export const useImpersonateBrandMutation = () =>
  useMutation({
    mutationFn: (brandId: string) => impersonateBrand(brandId),
  });

export const useUploadBrandLogoToS3Mutation = () =>
  useMutation({
    mutationFn: (file: File) => uploadBrandLogoToS3(file),
  });

export const useUploadBrandLogoMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      brandId,
      formData,
    }: {
      brandId: string;
      formData: FormData;
    }) => updateBrandLogo(brandId, formData),
    onSuccess: (_data, { brandId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.brands.byId(brandId),
      });
    },
  });
};
